import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';
import { app } from '../services/firebase.config';
import { doc, setDoc, getDoc, updateDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase.config';
import { registerFirebaseMessaging } from '../serviceWorkerRegistration';

// Inizializza Firebase Messaging
const messaging = getMessaging(app);

// VAPID key per le notifiche - usa variabile d'ambiente se disponibile
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'BI5CrLREUrVlhxKkYR8WQWfatE_vsKvEE2cKHoIFHp68_ETOi0Co2KSCYTDt4ygRl86qu782b3pXgftkEaNJKiY';

export interface NotificationSubscription {
  token: string;
  userId: string;
  enabled: boolean;
  createdAt: Date;
  lastUsed: Date;
}

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: Record<string, string>;
  requireInteraction?: boolean;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

export interface NotificationStatus {
  supported: boolean;
  permission: NotificationPermission;
  token: string | null;
  serviceWorkerRegistered: boolean;
}

// Salva il token nel database
async function saveTokenToDatabase(userId: string, token: string): Promise<void> {
  try {
    const tokenRef = doc(db, 'notificationSubscriptions', token);
    const tokenData: NotificationSubscription = {
      token,
      userId,
      enabled: true,
      createdAt: new Date(),
      lastUsed: new Date()
    };

    await setDoc(tokenRef, tokenData);
    console.log('Token salvato nel database per utente:', userId);
  } catch (error) {
    console.error('Errore nel salvataggio del token:', error);
    throw error;
  }
}

// Rimuovi il token dal database
async function removeTokenFromDatabase(token: string): Promise<void> {
  try {
    const tokenRef = doc(db, 'notificationSubscriptions', token);
    await updateDoc(tokenRef, { enabled: false });
    console.log('Token disabilitato nel database:', token);
  } catch (error) {
    console.error('Errore nella rimozione del token:', error);
    throw error;
  }
}

class NotificationService {
  private currentToken: string | null = null;
  private isSupported: boolean;
  private serviceWorkerRegistered: boolean = false;

  constructor() {
    this.isSupported = this.checkSupport();
    this.initializeServiceWorker();
  }

  // Inizializza il service worker per FCM
  private async initializeServiceWorker(): Promise<void> {
    try {
      if (this.isSupported && process.env.NODE_ENV === 'production') {
        await registerFirebaseMessaging();
        this.serviceWorkerRegistered = true;
        console.log('Service Worker FCM inizializzato');
      }
    } catch (error) {
      console.error('Errore nell\'inizializzazione del service worker FCM:', error);
    }
  }

  // Controlla se le notifiche sono supportate
  private checkSupport(): boolean {
    const supported = (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window &&
      !!messaging
    );
    
    console.log('Supporto notifiche:', {
      serviceWorker: 'serviceWorker' in navigator,
      pushManager: 'PushManager' in window,
      notification: 'Notification' in window,
      messaging: !!messaging,
      supported
    });
    
    return supported;
  }

  // Richiedi i permessi per le notifiche
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported) {
      console.warn('Le notifiche push non sono supportate in questo browser');
      return false;
    }

    try {
      console.log('Richiesta permessi notifiche...');
      const permission = await Notification.requestPermission();
      console.log('Permesso notifiche:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Errore nella richiesta permessi notifiche:', error);
      return false;
    }
  }

  // Ottieni il token FCM
  async getToken(): Promise<string | null> {
    if (!this.isSupported) {
      console.warn('Notifiche non supportate');
      return null;
    }

    try {
      // Controlla i permessi
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Permessi notifiche non concessi:', permission);
        return null;
      }

      console.log('Ottenimento token FCM con VAPID key:', VAPID_KEY.substring(0, 10) + '...');
      
      // Ottieni il token
      const token = await getToken(messaging, { vapidKey: VAPID_KEY });
      
      if (token) {
        this.currentToken = token;
        console.log('Token FCM ottenuto:', token.substring(0, 20) + '...');
        return token;
      } else {
        console.warn('Nessun token FCM disponibile');
        return null;
      }
    } catch (error) {
      console.error('Errore nell\'ottenimento del token FCM:', error);
      return null;
    }
  }

  // Elimina il token FCM
  async deleteToken(): Promise<void> {
    if (!this.currentToken) {
      console.warn('Nessun token da eliminare');
      return;
    }

    try {
      await deleteToken(messaging);
      await removeTokenFromDatabase(this.currentToken);
      this.currentToken = null;
      console.log('Token FCM eliminato');
    } catch (error) {
      console.error('Errore nell\'eliminazione del token FCM:', error);
      throw error;
    }
  }

  // Ottieni lo stato delle notifiche
  getNotificationStatus(): NotificationStatus {
    return {
      supported: this.isSupported,
      permission: 'Notification' in window ? Notification.permission : 'denied',
      token: this.currentToken,
      serviceWorkerRegistered: this.serviceWorkerRegistered
    };
  }

  // Inizializza le notifiche per un utente
  async initializeNotifications(userId: string): Promise<boolean> {
    console.log('Inizializzazione notifiche per utente:', userId);
    
    if (!this.isSupported) {
      console.warn('Notifiche non supportate');
      return false;
    }

    try {
      // Richiedi permessi
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        console.warn('Permessi notifiche negati');
        return false;
      }

      // Ottieni token
      const token = await this.getToken();
      if (!token) {
        console.warn('Impossibile ottenere token FCM');
        return false;
      }

      // Salva nel database
      await saveTokenToDatabase(userId, token);

      // Configura listener per notifiche in foreground
      this.setupForegroundListener();

      console.log('Notifiche inizializzate con successo per utente:', userId);
      return true;
    } catch (error) {
      console.error('Errore nell\'inizializzazione notifiche:', error);
      return false;
    }
  }

  // Configura listener per notifiche in foreground
  private setupForegroundListener(): void {
    try {
      onMessage(messaging, (payload) => {
        console.log('Notifica ricevuta in foreground:', payload);

        // Mostra notifica in-app
        if (payload.notification) {
          this.showInAppNotification({
            title: payload.notification.title || 'Nuova notifica',
            body: payload.notification.body || '',
            data: payload.data
          });
        }
      });
      console.log('Listener foreground configurato');
    } catch (error) {
      console.error('Errore nella configurazione listener foreground:', error);
    }
  }

  // Mostra notifica in-app
  private showInAppNotification(payload: NotificationPayload): void {
    try {
      // Qui puoi integrare con il tuo sistema di notifiche in-app
      // Per ora usiamo le notifiche native del browser
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(payload.title, {
          body: payload.body,
          icon: payload.icon || '/logo192.png',
          badge: payload.badge || '/logo192.png',
          tag: payload.tag,
          data: payload.data,
          requireInteraction: payload.requireInteraction,
          silent: false
        });

        // Gestisci il click sulla notifica
        notification.onclick = () => {
          window.focus();
          notification.close();
          
          // Naviga alla pagina specificata se presente
          if (payload.data?.url) {
            window.location.href = payload.data.url;
          }
        };

        console.log('Notifica in-app mostrata:', payload.title);
      }
    } catch (error) {
      console.error('Errore nella visualizzazione notifica in-app:', error);
    }
  }

  // Invia notifica a un utente specifico
  async sendNotificationToUser(userId: string, payload: NotificationPayload): Promise<void> {
    console.log('Invio notifica a utente:', userId, payload);
    
    // Questa funzione dovrebbe essere implementata lato server
    // Per ora simuliamo l'invio
    // In un'implementazione reale, qui chiameresti una Cloud Function
    // che invia la notifica tramite FCM
  }

  // Invia notifica di test tramite FCM (simulato)
  async sendTestNotificationViaFCM(userId: string): Promise<void> {
    try {
      console.log('Invio notifica di test per utente:', userId);
      
      // Simula l'invio di una notifica FCM
      const testPayload = {
        notification: {
          title: 'Test Notifica JobTracker',
          body: 'Questa è una notifica di test! 🎉',
          icon: '/logo192.png',
          badge: '/logo192.png'
        },
        data: {
          type: 'test',
          timestamp: new Date().toISOString(),
          url: '/dashboard'
        }
      };

      console.log('Simulazione invio notifica FCM:', testPayload);
      
      // In un'implementazione reale, qui chiameresti:
      // - Firebase Functions per inviare la notifica
      // - Oppure un endpoint API che usa FCM Admin SDK
      
      // Per ora mostriamo una notifica in-app
      this.showInAppNotification({
        title: testPayload.notification.title,
        body: testPayload.notification.body,
        icon: testPayload.notification.icon,
        badge: testPayload.notification.badge,
        data: testPayload.data
      });

      console.log('Notifica di test inviata con successo');

    } catch (error) {
      console.error('Errore nell\'invio notifica FCM:', error);
      throw error;
    }
  }

  // Verifica la configurazione delle notifiche
  async verifyConfiguration(): Promise<{
    supported: boolean;
    permission: string;
    token: boolean;
    serviceWorker: boolean;
    vapidKey: boolean;
  }> {
    const status = this.getNotificationStatus();
    
    return {
      supported: status.supported,
      permission: status.permission,
      token: !!status.token,
      serviceWorker: status.serviceWorkerRegistered,
      vapidKey: !!VAPID_KEY
    };
  }
}

// Esporta un'istanza singleton
export const notificationService = new NotificationService(); 